import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { StateAuditEntry } from '../../../models/stateAudit.model';

export function formatAuditTimestamp(ts: number): string {
  return new Date(ts).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatChangeValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val !== 'object') return String(val);
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

function formatFeeLine(f: Record<string, unknown>): string {
  const name = typeof f.name === 'string' ? f.name : 'Fee';
  const cost = f.cost != null ? String(f.cost) : '';
  if (f.type === 'percentage') return `${name} (${cost}%)`;
  return `${name} ($${cost})`;
}

type JsonRecord = Record<string, unknown>;

function feesById(arr: unknown): Map<string, JsonRecord> {
  const m = new Map<string, JsonRecord>();
  if (!Array.isArray(arr)) return m;
  for (const item of arr) {
    if (item && typeof item === 'object' && 'id' in item) {
      m.set(String((item as JsonRecord).id), item as JsonRecord);
    }
  }
  return m;
}

function snapshotForEquality(obj: JsonRecord): string {
  const keys = Object.keys(obj).sort();
  const sorted: JsonRecord = {};
  for (const k of keys) sorted[k] = obj[k];
  try {
    return JSON.stringify(sorted);
  } catch {
    return String(obj.id);
  }
}

function AuditDiffPanel({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        mt: 0.75,
        pl: 1.75,
        pr: 1,
        py: 1.25,
        borderLeft: `3px solid ${theme.palette.divider}`,
        borderRadius: '0 8px 8px 0',
        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
      })}
    >
      {children}
    </Box>
  );
}

function AuditSectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        fontWeight: 700,
        color: 'text.secondary',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        mb: 0.35,
      }}
    >
      {children}
    </Typography>
  );
}

function AuditFeeLine({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.primary" component="div" sx={{ py: 0.125, pl: 0.5 }}>
      {children}
    </Typography>
  );
}

const auditScalarSx = {
  mt: 0.75,
  pl: 1.5,
  py: 1,
  borderLeftWidth: 3,
  borderLeftStyle: 'solid' as const,
  borderLeftColor: 'divider',
  borderRadius: '0 8px 8px 0',
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  fontSize: '0.875rem',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};

type FeeAuditLabels = { added?: string; removed?: string; updated?: string };

function renderFeeListIndented(fees: unknown): ReactNode {
  if (!Array.isArray(fees) || fees.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
        No county fees yet.
      </Typography>
    );
  }
  return (
    <Box sx={{ pl: 0.5 }}>
      {(fees as unknown[]).map((f, i) =>
        f && typeof f === 'object' && 'name' in f ? (
          <AuditFeeLine key={i}>• {formatFeeLine(f as JsonRecord)}</AuditFeeLine>
        ) : (
          <AuditFeeLine key={i}>• {formatChangeValue(f)}</AuditFeeLine>
        ),
      )}
    </Box>
  );
}

function renderFeesAuditDiff(from: unknown, to: unknown, labels?: FeeAuditLabels): ReactNode {
  const L = {
    added: labels?.added ?? 'Added',
    removed: labels?.removed ?? 'Removed',
    updated: labels?.updated ?? 'Updated',
  };
  const fromMap = feesById(from);
  const toMap = feesById(to);
  const added: JsonRecord[] = [];
  const removed: JsonRecord[] = [];
  const changed: { before: JsonRecord; after: JsonRecord }[] = [];

  for (const [id, after] of toMap) {
    const before = fromMap.get(id);
    if (!before) added.push(after);
    else if (snapshotForEquality(before) !== snapshotForEquality(after)) {
      changed.push({ before, after });
    }
  }
  for (const [id, before] of fromMap) {
    if (!toMap.has(id)) removed.push(before);
  }

  if (added.length === 0 && removed.length === 0 && changed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
        No fee changes in this update.
      </Typography>
    );
  }

  return (
    <Box>
      {removed.length > 0 ? (
        <Box sx={{ mb: added.length || changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>{L.removed}</AuditSectionLabel>
          {removed.map((f) => (
            <AuditFeeLine key={String(f.id)}>• {formatFeeLine(f)}</AuditFeeLine>
          ))}
        </Box>
      ) : null}
      {added.length > 0 ? (
        <Box sx={{ mb: changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>{L.added}</AuditSectionLabel>
          {added.map((f) => (
            <AuditFeeLine key={String(f.id)}>• {formatFeeLine(f)}</AuditFeeLine>
          ))}
        </Box>
      ) : null}
      {changed.length > 0 ? (
        <Box>
          <AuditSectionLabel>{L.updated}</AuditSectionLabel>
          {changed.map(({ before, after }) => (
            <AuditFeeLine key={String(after.id)}>
              • {formatFeeLine(before)} → {formatFeeLine(after)}
            </AuditFeeLine>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function countiesById(arr: unknown): Map<string, JsonRecord> {
  const m = new Map<string, JsonRecord>();
  if (!Array.isArray(arr)) return m;
  for (const item of arr) {
    if (item && typeof item === 'object' && 'id' in item) {
      m.set(String((item as JsonRecord).id), item as JsonRecord);
    }
  }
  return m;
}

function countySnapshot(c: JsonRecord): string {
  const name = typeof c.name === 'string' ? c.name : '';
  const fees = feesById(c.fees);
  const feeParts = [...fees.keys()].sort().map((id) => `${id}:${snapshotForEquality(fees.get(id)!)}`);
  return `${name}|${feeParts.join(';')}`;
}

function renderCountiesAuditDiff(from: unknown, to: unknown): ReactNode {
  const fromM = countiesById(from);
  const toM = countiesById(to);
  const added: JsonRecord[] = [];
  const removed: JsonRecord[] = [];
  const changed: { before: JsonRecord; after: JsonRecord }[] = [];

  for (const [id, after] of toM) {
    const before = fromM.get(id);
    if (!before) added.push(after);
    else if (countySnapshot(before) !== countySnapshot(after)) {
      changed.push({ before, after });
    }
  }
  for (const [id, before] of fromM) {
    if (!toM.has(id)) removed.push(before);
  }

  if (added.length === 0 && removed.length === 0 && changed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
        No county changes in this update.
      </Typography>
    );
  }

  const countyTitle = (c: JsonRecord) =>
    typeof c.name === 'string' && c.name.length > 0 ? c.name : `County ${String(c.id)}`;

  const nestedFeeLabels: FeeAuditLabels = {
    added: 'New fees',
    removed: 'Removed fees',
    updated: 'Changed fees',
  };

  return (
    <Box>
      {removed.length > 0 ? (
        <Box sx={{ mb: added.length || changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>Removed counties</AuditSectionLabel>
          {removed.map((c) => (
            <AuditFeeLine key={String(c.id)}>• {countyTitle(c)}</AuditFeeLine>
          ))}
        </Box>
      ) : null}
      {added.length > 0 ? (
        <Box sx={{ mb: changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>New counties</AuditSectionLabel>
          {added.map((c) => (
            <Box key={String(c.id)} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ pl: 0.5, mb: 0.25, fontWeight: 600 }}>
                {countyTitle(c)}
              </Typography>
              {renderFeeListIndented(c.fees)}
            </Box>
          ))}
        </Box>
      ) : null}
      {changed.length > 0 ? (
        <Box>
          <AuditSectionLabel>Updated counties</AuditSectionLabel>
          {changed.map(({ before, after }) => {
            const nameBefore = typeof before.name === 'string' ? before.name : '';
            const nameAfter = typeof after.name === 'string' ? after.name : '';
            const nameChanged = nameBefore !== nameAfter;
            return (
              <Box
                key={String(after.id)}
                sx={(theme) => ({
                  mb: 1.25,
                  pl: 1,
                  ml: 0.25,
                  borderLeft: `2px solid ${alpha(theme.palette.divider, 0.9)}`,
                })}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {countyTitle(after)}
                </Typography>
                {nameChanged ? (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5, mb: 0.75 }}>
                    Renamed: {nameBefore || '—'} → {nameAfter || '—'}
                  </Typography>
                ) : null}
                <Box sx={{ pl: 0.25 }}>{renderFeesAuditDiff(before.fees, after.fees, nestedFeeLabels)}</Box>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}

export function auditEntrySummary(entry: StateAuditEntry): string {
  const who = entry.actorDisplayName || 'Someone';
  const when = formatAuditTimestamp(entry.timestamp);
  switch (entry.action) {
    case 'created':
      return `Fee configuration was created by ${who} on ${when}`;
    case 'deleted':
      return `${who} deleted this fee configuration on ${when}`;
    case 'updated':
      if (entry.changedFields && entry.changedFields.length > 0) {
        const fields = entry.changedFields.join(', ');
        return `${who} updated ${fields} on ${when}`;
      }
      return `${who} updated this fee configuration on ${when}`;
    default:
      return `${who} — ${when}`;
  }
}

export function auditEntryChanges(entry: StateAuditEntry): ReactNode {
  if (entry.action !== 'updated' || !entry.changes || Object.keys(entry.changes).length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
      {Object.entries(entry.changes).map(([field, { from, to }]) => (
        <li key={field}>
          <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {field}
            </Box>
            {field === 'fees' ? (
              <AuditDiffPanel>{renderFeesAuditDiff(from, to)}</AuditDiffPanel>
            ) : field === 'counties' ? (
              <AuditDiffPanel>{renderCountiesAuditDiff(from, to)}</AuditDiffPanel>
            ) : (
              <Box sx={auditScalarSx}>
                From: {formatChangeValue(from)}
                {'\n'}
                To: {formatChangeValue(to)}
              </Box>
            )}
          </Typography>
        </li>
      ))}
    </Box>
  );
}
